import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { crypto as cryptoShared } from '@photo-vault/shared';
import type { dto } from '@photo-vault/shared';
import { AntiAutofillHoneypot, Button, Card, Icon, Input, PageHeader } from '../../ui/index.js';
import { useAuth, useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { stashRecoveryCode } from './recoveryStorage.js';

type Step = 'idle' | 'verifying' | 'rewrapping' | 'persisting' | 'done';

export function ChangePassphrasePage() {
  const navigate = useNavigate();
  const session = useAuthSession();
  const { setSession } = useAuth();
  const { metadata } = useInfra();

  const [currentPassphrase, setCurrentPassphrase] = useState('');
  const [newPassphrase, setNewPassphrase] = useState('');
  const [newConfirm, setNewConfirm] = useState('');
  const [rotateRecovery, setRotateRecovery] = useState(false);
  const [step, setStep] = useState<Step>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (newPassphrase !== newConfirm) {
      setError('Le due passphrase non coincidono.');
      return;
    }
    if (newPassphrase.length < 12) {
      setError('La nuova passphrase deve essere di almeno 12 caratteri.');
      return;
    }
    if (newPassphrase === currentPassphrase) {
      setError('La nuova passphrase deve essere diversa dalla precedente.');
      return;
    }

    try {
      setStep('verifying');
      const { verifierB64 } = await cryptoShared.deriveMasterFromStoredSalt(
        currentPassphrase,
        session.saltB64,
      );
      const verify = await metadata.verifyPassphrase(
        { verifierB64 } satisfies dto.VerifyPassphraseRequest,
        session.jwt,
      );
      if (!verify.ok) throw new Error('Passphrase corrente non corretta.');

      setStep('rewrapping');
      const rebuilt = await cryptoShared.rebuildKeyMaterialForNewPassphrase(
        session.rsaPriv,
        newPassphrase,
      );
      const recovery = rotateRecovery
        ? await cryptoShared.rebuildRecoveryMaterial(session.rsaPriv)
        : null;

      // The verifier we derived above doubles as the server-side proof that we
      // know the current passphrase. The verifyPassphrase call stays for the
      // early, friendlier error, but the authoritative check is the conditional
      // write behind changePassphrase.
      const proof = { kind: 'passphrase', currentVerifierB64: verifierB64 } as const;

      const body: dto.ChangePassphraseRequest = recovery
        ? {
            saltB64: rebuilt.saltB64,
            wrappedPriv: rebuilt.wrappedPriv,
            verifierB64: rebuilt.verifierB64,
            saltRecoveryB64: recovery.saltRecoveryB64,
            wrappedPrivRecovery: recovery.wrappedPrivRecovery,
            verifierRecoveryB64: recovery.verifierRecoveryB64,
            proof,
          }
        : {
            saltB64: rebuilt.saltB64,
            wrappedPriv: rebuilt.wrappedPriv,
            verifierB64: rebuilt.verifierB64,
            proof,
          };

      setStep('persisting');
      await metadata.changePassphrase(body, session.jwt);

      setSession({
        ...session,
        masterKey: rebuilt.masterKey,
        saltB64: rebuilt.saltB64,
      });

      setStep('done');
      if (recovery) {
        stashRecoveryCode(recovery.recoveryCode, 'rotated');
        navigate('/recovery-code', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setStep('idle');
      setError(err instanceof Error ? err.message : 'Cambio passphrase fallito.');
    }
  }

  const busy = step !== 'idle' && step !== 'done';

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-7">
      <PageHeader
        eyebrow="chiave master"
        title={
          <>
            Cambia la tua <span className="accent-text">passphrase</span>
          </>
        }
        description={
          <>
            La nuova passphrase deriva una nuova chiave master e ricifra la tua chiave RSA privata.
            La coppia RSA <strong>non</strong> cambia: album e condivisioni esistenti restano
            accessibili.
          </>
        }
      />
      <div className="rounded-xl border border-outline bg-surface-container-low px-4 py-3 text-sm text-on-surface-variant">
        <p>
          <strong className="text-on-surface">Limite di sicurezza.</strong> Se la passphrase
          corrente fosse stata debole e qualcuno avesse esfiltrato il vecchio materiale dal server,
          questo cambio non revoca quel rischio: solo la rotazione completa dell&apos;identità
          (operazione separata, prevista in fase 7) chiude la finestra.
        </p>
      </div>

      <Card className="flex flex-col gap-6">
        <form className="flex flex-col gap-3" onSubmit={handleSubmit} autoComplete="off">
          <AntiAutofillHoneypot />
          <Input
            type="password"
            label="Passphrase corrente"
            value={currentPassphrase}
            onChange={(e) => setCurrentPassphrase(e.target.value)}
            leftIcon={<Icon name="lock_open" className="text-base" />}
            autoComplete="current-password"
            required
          />
          <Input
            type="password"
            label="Nuova passphrase"
            placeholder="min. 12 caratteri, meglio se ≥ 6 parole"
            value={newPassphrase}
            onChange={(e) => setNewPassphrase(e.target.value)}
            leftIcon={<Icon name="lock" className="text-base" />}
            autoComplete="new-password"
            required
            hint="Più è lunga e casuale, più resiste a brute-force offline."
          />
          <Input
            type="password"
            label="Conferma nuova passphrase"
            value={newConfirm}
            onChange={(e) => setNewConfirm(e.target.value)}
            leftIcon={<Icon name="check" className="text-base" />}
            autoComplete="new-password"
            required
          />

          <label className="mt-2 inline-flex cursor-pointer items-start gap-3 rounded-lg border border-outline bg-surface-container-low px-4 py-3 text-sm text-on-surface-variant">
            <input
              type="checkbox"
              checked={rotateRecovery}
              onChange={(e) => setRotateRecovery(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-outline bg-surface-container-low accent-cyan"
            />
            <span>
              Rigenera anche il <strong className="text-on-surface">codice di recupero</strong>. Il
              vecchio codice non funzionerà più: ricordati di aggiornarlo dove l&apos;hai salvato.
            </span>
          </label>

          {error ? (
            <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            size="lg"
            loading={busy}
            rightIcon={!busy ? <Icon name="arrow_forward" className="text-base" /> : null}
          >
            {busy ? labelForStep(step) : 'Aggiorna passphrase'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate('/', { replace: true })}
            disabled={busy}
          >
            Annulla
          </Button>
        </form>
      </Card>
    </div>
  );
}

function labelForStep(step: Step): string {
  switch (step) {
    case 'verifying':
      return 'Verifico la passphrase corrente…';
    case 'rewrapping':
      return 'Ricifro la chiave RSA…';
    case 'persisting':
      return 'Salvo i nuovi metadati…';
    case 'done':
      return 'Fatto';
    default:
      return 'Attendi…';
  }
}
